/**
 * SignIn Component for Red Setter Homes Portal
 * Passwordless authentication with Magic Link and Google Sign-In
 * Routes users to Client or Admin dashboard based on Airtable role
 *
 * @component
 */

import React, { useState, useEffect } from "react";
import {
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { FaQuestionCircle, FaShieldAlt } from "react-icons/fa";
import { auth } from "../firebase";
import InfoModal from "../components/InfoModal";
import {
  PRIVACY_POLICY_CONTENT,
  TERMS_CONTENT,
} from "../components/legalContent";
import "./SignIn.css";

/**
 * Main SignIn Component
 * Handles both Magic Link and Google & Microsoft authentication
 * Checks user role from Airtable (Client vs Team) and routes accordingly
 */
function SignIn() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  /**
   * Handles the click event for the "Can't find the email?" link.
   * Opens the info modal with email-related instructions.
   */
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalMessage, setInfoModalMessage] = useState(null);
  const openInfoModal = (messageKey) => {
    setInfoModalMessage(messageKey);
    setIsInfoModalOpen(true);
  };

  /**
   * Tracks which legal modal is open, if any.
   * @type {[null | 'privacy' | 'terms', Function]}
   */
  const [activeLegalModal, setActiveLegalModal] = useState(null);

  const isPrivacyModal = activeLegalModal === "privacy";
  const legalModalTitle = isPrivacyModal
    ? "Privacy Policy"
    : "Terms and Conditions";
  const legalModalContent = isPrivacyModal
    ? PRIVACY_POLICY_CONTENT
    : TERMS_CONTENT;

  // Remove navbar body padding on mount, restore on unmount
  useEffect(() => {
    document.body.style.paddingBottom = "0";

    return () => {
      document.body.style.paddingBottom = ""; // Restore default
    };
  }, []);

  /**
   * Check for magic link sign-in completion
   * Handles when user clicks the link in their email
   */
  useEffect(() => {
    const completeMagicLinkSignIn = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        console.log("Magic link detected, completing sign-in...");

        // Get the email from localStorage or prompt
        let emailForSignIn = window.localStorage.getItem("emailForSignIn");

        if (!emailForSignIn) {
          emailForSignIn = window.prompt(
            "Please provide your email for confirmation",
          );
        }

        if (emailForSignIn) {
          try {
            setLoading(true);
            const result = await signInWithEmailLink(
              auth,
              emailForSignIn,
              window.location.href,
            );
            console.log("Magic link sign-in successful:", result.user.email);

            // Clear email from storage
            window.localStorage.removeItem("emailForSignIn");

            // Clean up the URL to remove the magic link parameters
            window.history.replaceState({}, document.title, "/signin");

            // Auth state change will trigger App.js to redirect
            console.log("Auth completed - App.js will handle redirect");
          } catch (error) {
            console.error("Magic link sign-in error:", error);
            setError(getErrorMessage(error.code));
          } finally {
            setLoading(false);
            setCheckingAuth(false);
          }
        } else {
          setCheckingAuth(false);
        }
      } else {
        setCheckingAuth(false);
      }
    };

    completeMagicLinkSignIn();
  }, []);

  /**
   * Handle Magic Link sign-in
   * Sends email with sign-in link
   */
  const handleMagicLinkSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const actionCodeSettings = {
      url: window.location.origin + "/signin",
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      // Save email to complete sign-in after clicking link
      window.localStorage.setItem("emailForSignIn", email);
      setMagicLinkSent(true);
    } catch (error) {
      console.error("Magic link error:", error);
      setError(getErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle Google Sign-In
   */
  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      await signInWithPopup(auth, provider);

      console.log("Google sign-in successful - App.js will handle redirect");
      // Auth state change will trigger App.js to redirect
    } catch (error) {
      console.error("Google sign-in error:", error);
      setError(getErrorMessage(error.code));
      setLoading(false);
    }
  };

  /**
   * Handle Microsoft Sign-In
   * Uses Firebase OAuthProvider with the "microsoft.com" provider ID.
   * Custom parameters force account selection and use the "common" tenant
   * so both personal and work/school Microsoft accounts can authenticate.
   */
  const handleMicrosoftSignIn = async () => {
    setError("");
    setLoading(true);

    try {
      const provider = new OAuthProvider("microsoft.com");
      provider.setCustomParameters({
        prompt: "select_account",
        tenant: "common",
      });
      await signInWithPopup(auth, provider);

      console.log("Microsoft sign-in successful - App.js will handle redirect");
      // Auth state change will trigger App.js to redirect
    } catch (error) {
      console.error("Microsoft sign-in error:", error);
      setError(getErrorMessage(error.code));
      setLoading(false);
    }
  };

  /**
   * Convert Firebase error codes to user-friendly messages
   * @param {string} errorCode - Firebase error code
   * @returns {string} User-friendly error message
   */
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/user-not-found":
        return "No account found with this email. Please contact Red Setter Homes.";
      case "auth/too-many-requests":
        return "Too many attempts. Please try again later.";
      case "auth/invalid-action-code":
        return "This sign-in link is invalid or has expired. Please request a new one.";
      case "auth/popup-closed-by-user":
        return "Sign-in was cancelled. Please try again.";
      default:
        return "An error occurred. Please try again or contact support.";
    }
  };

  // Show loading screen while checking authentication
  if (checkingAuth) {
    return (
      <div className="signin-container">
        {/* Background Image (mobile only) */}
        <div className="signin-background-image"></div>
        <div className="signin-background-overlay"></div>

        {/* Mobile Header (over image) */}
        <div className="signin-mobile-header">
          <img
            src="/rsh-logo.png"
            alt="Red Setter Homes"
            className="signin-mobile-logo"
          />
          <h1 className="signin-mobile-title">Project Portal</h1>
          <p className="signin-mobile-tagline">
            To access your project, sign in with the email you use to correspond
            with us.
          </p>
        </div>

        {/* Loading Message */}
        <div className="signin-content">
          <div className="signin-loading">
            <div className="signin-spinner"></div>
            <p>Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show confirmation screen after magic link is sent
  if (magicLinkSent) {
    return (
      <div className="signin-container">
        {/* Background Image (mobile only) */}
        <div className="signin-background-image"></div>
        <div className="signin-background-overlay"></div>

        {/* Mobile Header (over image) */}
        <div className="signin-mobile-header">
          <img
            src="/rsh-logo.png"
            alt="Red Setter Homes"
            className="signin-mobile-logo"
          />
          <h1 className="signin-mobile-title">Project Portal</h1>
          <p className="signin-mobile-tagline">
            To access your project, sign in with the email you use to correspond
            with us.
          </p>
        </div>

        {/* Success Message */}
        <div className="signin-content">
          <div className="signin-form-container">
            {/* Desktop Header */}
            <div className="signin-header">
              <img
                src="/rsh-logo.png"
                alt="Red Setter Homes"
                className="signin-logo"
              />
            </div>

            <div className="signin-success-message">
              <div className="signin-success-icon">
                <svg
                  id="fi_7286142"
                  height="48"
                  viewBox="0 0 512 512"
                  width="48"
                  xmlns="http://www.w3.org/2000/svg"
                  data-name="Layer 1"
                >
                  <rect
                    fill="#e57e25"
                    height="305.613"
                    rx="25.31"
                    width="446"
                  ></rect>
                  <path
                    d="m435.739 300.212a26.13 26.13 0 0 0 10.261-20.778v-253.255a26.179 26.179 0 0 0 -26.179-26.179h-253.313l268.692 300.014z"
                    fill="#f29c1f"
                  ></path>
                  <path
                    d="m279.492 0h-253.313a26.179 26.179 0 0 0 -26.179 26.179v253.255a26.124 26.124 0 0 0 9.7 20.334l1.1.246z"
                    fill="#f29c1f"
                  ></path>
                  <path
                    d="m419.821 0h-393.642a26.179 26.179 0 0 0 -26.179 26.179v10.063l188.916 130.177a60.068 60.068 0 0 0 68.168 0l188.916-130.177v-10.063a26.179 26.179 0 0 0 -26.179-26.179z"
                    fill="#f0c419"
                  ></path>
                  <path
                    d="m116.126 512a8 8 0 0 1 -5.157-14.119l110.588-91.916a8 8 0 1 1 10.306 12.235l-110.587 91.919a7.97 7.97 0 0 1 -5.15 1.881z"
                    fill="#ff5364"
                  ></path>
                  <path
                    d="m209.927 494.588a8 8 0 0 1 -5.275-14.018l27.222-24.87a8 8 0 1 1 10.543 12.035l-27.217 24.87a7.967 7.967 0 0 1 -5.273 1.983z"
                    fill="#35acef"
                  ></path>
                  <path
                    d="m239.598 336.348 50.471 120.895 92.207-138.322 129.63-123.515z"
                    fill="#285680"
                  ></path>
                  <path
                    d="m511.906 194.516-266.03 155.177-78.277-20.307z"
                    fill="#7ed0fc"
                  ></path>
                  <path
                    d="m418.218 297.138-128.149 160.105 15.123-91.701z"
                    fill="#35acef"
                  ></path>
                  <path
                    d="m511.906 195.406c.073-.227.169-.4 0-.232l-206.714 170.368 128.054 34.858z"
                    fill="#7ed0fc"
                  ></path>
                </svg>
              </div>
              <h2>Check Your Email!</h2>
              <p className="signin-success-text">
                We've sent a secure sign-in link to:
              </p>
              <p className="signin-email-display">{email}</p>
              <p className="signin-success-note">
                <span onClick={() => openInfoModal("cantFindEmail")}>
                  Can't find the email?
                </span>
              </p>
              <p className="signin-success-instruction">
                You can close this page.
              </p>
            </div>

            <button
              onClick={() => {
                setMagicLinkSent(false);
                setEmail("");
              }}
              className="signin-back-button"
            >
              ← Back to Sign In
            </button>
          </div>
        </div>

        {/* Right Side - Image (desktop only) */}
        <div className="signin-image-panel">
          <div className="signin-image-overlay"></div>
          <div className="signin-image-content">
            <h2>Building Together, Every Step of the Way</h2>
            <p>
              Track your project progress, make selections, and stay connected
              with your Red Setter Homes team.
            </p>
          </div>
        </div>

        <InfoModal
          isOpen={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          title="Can't Find the Email?"
          icon={<FaQuestionCircle />}
          message={infoModalMessage}
        />
      </div>
    );
  }

  // Main sign-in form
  return (
    <div className="signin-container">
      {/* Background Image (mobile only) */}
      <div className="signin-background-image"></div>
      <div className="signin-background-overlay"></div>

      {/* Mobile Header (over image) */}
      <div className="signin-mobile-header">
        <img
          src="/rsh-logo.png"
          alt="Red Setter Homes"
          className="signin-mobile-logo"
        />
        <h1 className="signin-mobile-title">Project Portal</h1>
        <p className="signin-mobile-tagline">
          To access your project, sign in with the email you use to correspond
          with us.
        </p>
      </div>

      {/* Left Side - Form (Desktop) / Bottom Card (Mobile) */}
      <div className="signin-content">
        <div className="signin-form-container">
          {/* Desktop Header */}
          <div className="signin-header">
            <img
              src="/rsh-logo.png"
              alt="Red Setter Homes"
              className="signin-logo"
            />
            <h1 className="signin-title">Project Portal</h1>
            <p className="signin-tagline">
              To access your project, sign in with the email you use to
              correspond with us.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="signin-error">
              <span className="signin-error-icon">⚠️</span>
              {error}
            </div>
          )}

          {/* Magic Link Form */}
          <form onSubmit={handleMagicLinkSignIn} className="signin-form">
            <div className="signin-form-group">
              <label htmlFor="email" className="signin-label">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="signin-input"
                placeholder="your@email.com"
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="signin-button signin-button-primary"
            >
              {loading ? (
                <>
                  <span className="signin-button-spinner"></span>
                  Sending...
                </>
              ) : (
                <>Send Magic Link</>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="signin-divider">
            <span>or</span>
          </div>
          <div className="signin-options-buttons">
            {/* Google Sign In */}
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="signin-button signin-button-auth"
            >
              <svg
                className="signin-auth-icon"
                width="18"
                height="18"
                viewBox="0 0 18 18"
              >
                <path
                  fill="#4285F4"
                  d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                />
                <path
                  fill="#34A853"
                  d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
                />
                <path
                  fill="#FBBC05"
                  d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"
                />
                <path
                  fill="#EA4335"
                  d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                />
              </svg>
              <span className="hide-sm">Sign in with Google</span>
              <span className="show-sm">Google</span>
            </button>

            {/* Microsoft Sign In */}
            <button
              onClick={handleMicrosoftSignIn}
              disabled={loading}
              className="signin-button signin-button-auth"
            >
              <svg
                className="signin-auth-icon"
                width="18"
                height="18"
                viewBox="0 0 18 18"
              >
                <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
                <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
                <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
                <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
              </svg>
              <span className="hide-sm">Sign in with Microsoft</span>
              <span className="show-sm">Microsoft</span>
            </button>
          </div>
          <div className="signin-help">
            <p>
              Need help?{" "}
              <a href="mailto:info@redsetterhomes.com" className="signin-link">
                Contact Support
              </a>
            </p>
          </div>

          {/* Footer */}
          <div className="signin-footer">
            <p className="signin-legal-links">
              <span
                className="signin-link signin-legal-link"
                role="button"
                tabIndex={0}
                onClick={() => setActiveLegalModal("privacy")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveLegalModal("privacy");
                  }
                }}
              >
                Privacy Policy
              </span>
              {" · "}
              <span
                className="signin-link signin-legal-link"
                role="button"
                tabIndex={0}
                onClick={() => setActiveLegalModal("terms")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveLegalModal("terms");
                  }
                }}
              >
                Terms and Conditions
              </span>
            </p>
          </div>
        </div>
      </div>

      <InfoModal
        isOpen={activeLegalModal !== null}
        onClose={() => setActiveLegalModal(null)}
        title={legalModalTitle}
        icon={<FaShieldAlt />}
      >
        {legalModalContent}
      </InfoModal>

      {/* Right Side - Image (desktop only) */}
      <div className="signin-image-panel">
        <div className="signin-image-overlay"></div>
        <div className="signin-image-content">
          <h2>Building Together, Every Step of the Way</h2>
          <p>
            Track your project progress, make selections, and stay connected
            with your Red Setter Homes team.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignIn;
